'use client';

import { useState, useRef, useEffect, useCallback, useContext, createContext } from 'react';
import { Pencil, Trash2, Check, X, SmilePlus, Reply } from 'lucide-react';
import { cn, truncateAddress, formatTimeAgo } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { AvatarWithFlair, LevelBadge } from '@/components/prelaunch/avatar-with-flair';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { ExternalLinkText } from './external-link-text';

/**
 * Touch-friendly "active message" coordinator.
 *
 * Hover affordances don't fire on touch, so on phones / tablets the
 * existing `group-hover:opacity-100` action row was permanently invisible
 * — users couldn't reach edit / delete / react / reply at all. We solve
 * it by letting the user tap a message bubble to toggle its action row,
 * with the active id stored in this context so only one message's
 * actions are surfaced at a time. Hover continues to work unchanged on
 * `lg+` because we additively reveal actions whenever EITHER hover or
 * `data-actions-active="true"` are present (see the action row's
 * `group-data-[actions-active=true]:opacity-100` class).
 *
 * Provider lives inside `ChatPanel` (one per message list) so the
 * "active id" doesn't leak across distinct chat surfaces (e.g. boardroom
 * vs a per-board panel rendered side-by-side in some debug surface).
 */
interface ChatMessageActionsContextValue {
  activeId: number | null;
  setActiveId: (id: number | null) => void;
}
const ChatMessageActionsContext = createContext<ChatMessageActionsContextValue | null>(null);

export function ChatMessageActionsProvider({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = useState<number | null>(null);
  return (
    <ChatMessageActionsContext.Provider value={{ activeId, setActiveId }}>
      {children}
    </ChatMessageActionsContext.Provider>
  );
}

export interface Reactor {
  address: string;
  display_name: string | null;
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  reacted: boolean;
  reactors?: Reactor[];
}

export interface ReplyToData {
  id: number;
  message: string;
  address: string;
  display_name: string | null;
}

export interface ChatMessageData {
  id: number;
  address: string;
  message: string;
  created_at: number;
  edited_at: number | null;
  display_name: string | null;
  avatar_url: string | null;
  level: number;
  held_since: number | null;
  reactions: ReactionSummary[];
  reply_to?: ReplyToData | null;
  /**
   * `true` when `address` is the onchain creator of the chat room's
   * grid. Stamped by the chat API routes (`/api/chat` GET + POST) via
   * `getGridCreator`; see `ChatMessage.is_creator` in
   * `lib/db/chat.ts`. Used here to render a "Creator" chip alongside
   * the display name so viewers can distinguish the grid's author
   * from other holders without hovering the avatar.
   */
  is_creator?: boolean;
  /**
   * `true` when `address` is on the team's Dev allow-list (`isDevAddress`
   * in `lib/admin/allow-list.ts`). Stamped by the chat API routes the
   * same way `is_creator` is — never persisted. Renders a cyan "Dev"
   * chip ahead of `Creator` so viewers can recognise the team in any
   * room, including ones the dev hasn't claimed a seat on.
   */
  is_dev?: boolean;
}

const PRESET_EMOJIS = ["👍", "❤️", "😂", "🔥", "👀", "🎉"];

const EDIT_WINDOW_MS = 5 * 60 * 1000;

function formatHolderSince(timestampMs: number): string {
  const d = new Date(timestampMs);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ChatMessageProps {
  msg: ChatMessageData;
  isOwn: boolean;
  /**
   * `true` when the viewer is allowed to delete *any* message in this
   * room (currently: `isDevAddress(viewer)`). Wired from `ChatPanel`'s
   * `verifyData.is_dev`. Surfaces the trash button on non-own messages
   * and lets the existing delete-confirm flow drive the moderator
   * delete; the server enforces the actual permission via
   * `softDeleteMessage(id, null)` for devs.
   */
  canModerate?: boolean;
  onEdit?: (id: number, newText: string) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
  onReact?: (id: number, emoji: string) => Promise<void>;
  onReply?: (msg: ChatMessageData) => void;
  onScrollToMessage?: (id: number) => void;
}

export function ChatMessage({ msg, isOwn, canModerate = false, onEdit, onDelete, onReact, onReply, onScrollToMessage }: ChatMessageProps) {
  const displayName = msg.display_name || truncateAddress(msg.address);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(msg.message);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const profileModal = useProfileModalOptional();
  const openProfile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      profileModal?.openProfile(msg.address);
    },
    [profileModal, msg.address],
  );

  const actionsCtx = useContext(ChatMessageActionsContext);
  const isActionsActive = actionsCtx?.activeId === msg.id;
  // Tap-to-toggle. We bail when the tap originated on an interactive
  // element inside the bubble (links via `<ExternalLinkText>`, the
  // reaction buttons rendered below, etc.) so a tap on a hyperlink or
  // an existing reaction doesn't double-toggle the action row.
  const handleBubbleTap = useCallback(
    (e: React.MouseEvent) => {
      if (!actionsCtx) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('a, button, textarea, input, select, [role="button"]')) return;
      actionsCtx.setActiveId(isActionsActive ? null : msg.id);
    },
    [actionsCtx, isActionsActive, msg.id],
  );

  const canEdit = isOwn && Date.now() - msg.created_at < EDIT_WINDOW_MS;
  const canShowTrash = isOwn || canModerate;

  useEffect(() => {
    if (editing && editRef.current) {
      const el = editRef.current;
      el.focus();
      el.selectionStart = el.value.length;
      el.style.height = 'auto';
      const h = Math.min(el.scrollHeight, 80);
      el.style.height = h + 'px';
      el.style.overflow = el.scrollHeight > 80 ? 'auto' : 'hidden';
    }
  }, [editing]);

  const handleSaveEdit = useCallback(async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === msg.message || saving) return;
    setSaving(true);
    try {
      await onEdit?.(msg.id, trimmed);
      setEditing(false);
    } finally { setSaving(false); }
  }, [editText, msg.message, msg.id, saving, onEdit]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    try { await onDelete?.(msg.id); } finally { setSaving(false); }
  }, [msg.id, onDelete]);

  const handleReact = useCallback(async (emoji: string) => {
    setShowReactionPicker(false);
    await onReact?.(msg.id, emoji);
  }, [msg.id, onReact]);

  useEffect(() => {
    if (!showReactionPicker) return;
    const handler = (e: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target as Node)) {
        setShowReactionPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReactionPicker]);

  const reactions = msg.reactions ?? [];

  return (
    <div
      data-message-id={msg.id}
      data-actions-active={isActionsActive ? 'true' : undefined}
      className={cn('flex gap-2 px-3 py-1.5 group transition-colors duration-500 rounded-lg', isOwn && 'flex-row-reverse')}
    >
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={openProfile}
            className="shrink-0 mt-0.5 cursor-pointer"
            aria-label={`Open profile for ${displayName}`}
          >
            <AvatarWithFlair
              avatarUrl={msg.avatar_url || undefined}
              address={msg.address}
              level={msg.level}
              size="xs"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side={isOwn ? 'left' : 'right'} className="p-2.5">
          <div className="flex items-start gap-3 min-w-[180px]">
            <div className="shrink-0">
              <AvatarWithFlair
                avatarUrl={msg.avatar_url || undefined}
                address={msg.address}
                level={msg.level}
                size="sm"
              />
            </div>
            <div className="flex-1 min-w-0 space-y-0.5">
              <button
                type="button"
                onClick={openProfile}
                className={cn(
                  'font-medium text-sm truncate block hover:underline text-left',
                  isOwn
                    ? 'text-amber-100/90 hover:text-amber-50'
                    : 'text-zinc-100/90 hover:text-amber-100',
                )}
              >
                {displayName}
              </button>
              {(msg.is_dev || msg.is_creator) && (
                <div className="flex flex-wrap gap-1">
                  {msg.is_dev && <DevChip />}
                  {msg.is_creator && <CreatorChip />}
                </div>
              )}
              {msg.level > 0 && <LevelBadge level={msg.level} className="text-[10px]" />}
              {msg.held_since && (
                <p className="text-[10px] text-muted-foreground">
                  Holder since {formatHolderSince(msg.held_since)}
                </p>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>

      {/* `min-w-0` is required: without it, a flex item's default
          `min-width: auto` resolves to its min-content size, so a single
          long unbreakable token (long contract address, glued URL,
          `aaaaa…`) grows the column past `max-w-[75%]` and pushes the
          row off-screen — the chat message becomes unreadable until you
          horizontally scroll. With `min-w-0`, `max-w-[75%]` constrains
          the column and `[overflow-wrap:anywhere]` on the bubble below
          handles the actual character-level break. */}
      <div className={cn('flex flex-col max-w-[75%] min-w-0', isOwn && 'items-end')}>
        <div className="flex items-baseline gap-1.5 mb-0.5">
          <span
            className={cn(
              'text-[11px] truncate max-w-[140px]',
              isOwn ? 'font-medium text-amber-200/80' : 'font-medium text-foreground/80',
            )}
          >
            {displayName}
          </span>
          {msg.is_dev && <DevChip />}
          {msg.is_creator && <CreatorChip />}
          <span
            className={cn(
              'text-[10px] shrink-0',
              isOwn ? 'text-amber-200/45' : 'text-muted-foreground/60',
            )}
          >
            {formatTimeAgo(msg.created_at)}
          </span>
          {msg.edited_at && (
            <span className="text-[9px] text-muted-foreground/40 italic shrink-0">(edited)</span>
          )}
        </div>

        {msg.reply_to && (
          <button
            onClick={() => onScrollToMessage?.(msg.reply_to!.id)}
            className={cn(
              'rounded-lg px-2.5 py-1 mb-0.5 text-[11px] leading-snug border cursor-pointer transition-colors max-w-full',
              isOwn
                ? 'text-right border-amber-400/25 bg-amber-950/20 hover:bg-amber-950/35'
                : 'border-border/60 bg-muted/30 hover:bg-muted/50 text-left',
            )}
          >
            <span className="font-medium text-muted-foreground">
              {msg.reply_to.display_name || truncateAddress(msg.reply_to.address)}
            </span>
            <p className="text-muted-foreground/70 truncate">
              {msg.reply_to.message.length > 80 ? msg.reply_to.message.slice(0, 80) + '...' : msg.reply_to.message}
            </p>
          </button>
        )}

        {editing ? (
          <div className="w-full space-y-1">
            <textarea
              ref={editRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value.slice(0, 500))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSaveEdit(); }
                if (e.key === 'Escape') { setEditing(false); setEditText(msg.message); }
              }}
              className={cn(
                'w-full resize-none rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 max-h-[80px] min-h-[32px]',
                isOwn
                  ? 'bg-black/25 border border-amber-400/25 text-zinc-100 focus:ring-amber-400/45'
                  : 'bg-muted/30 border border-border focus:ring-accent-blue',
              )}
              style={{ height: 'auto' }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                const h = Math.min(el.scrollHeight, 80);
                el.style.height = h + 'px';
                el.style.overflow = el.scrollHeight > 80 ? 'auto' : 'hidden';
              }}
            />
            <div className="flex gap-1 justify-end">
              <button
                onClick={() => { setEditing(false); setEditText(msg.message); }}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                disabled={saving}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editText.trim() || editText.trim() === msg.message}
                className={cn(
                  'p-1 rounded transition-colors disabled:opacity-40',
                  isOwn
                    ? 'text-amber-200/90 hover:bg-amber-500/15'
                    : 'text-amber-300/90 hover:bg-amber-500/15',
                )}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div
              onClick={handleBubbleTap}
              className={cn(
                'rounded-xl px-3 py-1.5 text-sm leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap select-text',
                // `cursor-pointer` on touch (lg:cursor-default keeps the
                // text-select cursor on desktop) hints to the user that
                // the bubble itself is interactive — that's how they
                // surface edit / delete / react / reply on touch.
                'cursor-pointer lg:cursor-text',
                isOwn
                  ? 'bg-amber-500/12 border border-amber-400/35 text-zinc-100 rounded-tr-sm shadow-[inset_0_1px_0_0_rgba(255,215,120,0.06)]'
                  : 'bg-card border border-border rounded-tl-sm'
              )}
            >
              <ExternalLinkText text={msg.message} isOwn={isOwn} />
            </div>

            {!confirming && !showReactionPicker && (
              <div className={cn(
                'absolute top-0 flex gap-0.5 opacity-0 transition-opacity',
                'group-hover:opacity-100 group-data-[actions-active=true]:opacity-100',
                isOwn ? 'right-full mr-1' : 'left-full ml-1',
              )}>
                {!isOwn && onReply && (
                  <button
                    onClick={() => onReply(msg)}
                    className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <Reply className="h-3 w-3" />
                  </button>
                )}
                {isOwn && canEdit && (
                  <button
                    onClick={() => { setEditText(msg.message); setEditing(true); }}
                    className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            <div
              className={cn(
                'flex flex-wrap items-center gap-1 mt-1.5 min-h-[26px] w-0 min-w-full overflow-visible',
                isOwn && 'justify-end',
              )}
            >
              {reactions.map((r) => (
                <Tooltip key={r.emoji} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleReact(r.emoji)}
                      className={cn(
                        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs border transition-colors h-[26px]',
                        r.reacted
                          ? 'bg-amber-500/18 border-amber-400/45 text-amber-50'
                          : 'bg-muted/40 border-border text-muted-foreground hover:bg-muted/60'
                      )}
                    >
                      <span className="leading-none">{r.emoji}</span>
                      <span className="text-[10px] font-medium tabular-nums">{r.count}</span>
                    </button>
                  </TooltipTrigger>
                  {r.reactors && r.reactors.length > 0 && (
                    <TooltipContent side="top" className="px-2 py-1.5 text-[11px] max-w-[200px]">
                      <p className="leading-snug">
                        {r.reactors.map((u) => u.display_name || truncateAddress(u.address)).join(', ')}
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
              {!isOwn && !confirming && (
                showReactionPicker ? (
                  <div ref={reactionPickerRef} className="inline-flex items-center gap-0.5 bg-background border border-border rounded-full px-1.5 shadow-sm h-[26px]">
                    {PRESET_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReact(emoji)}
                        className="p-0.5 rounded hover:bg-muted/50 transition-colors text-sm leading-none"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowReactionPicker(true)}
                    className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors opacity-0 group-hover:opacity-100 group-data-[actions-active=true]:opacity-100 h-[26px] w-[26px]"
                  >
                    <SmilePlus className="h-3.5 w-3.5" />
                  </button>
                )
              )}
              {canShowTrash && (
                confirming ? (
                  <div className="inline-flex items-center gap-0.5 bg-background border border-border rounded-full px-1.5 shadow-sm h-[26px]">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap px-1">
                      {isOwn ? 'Delete?' : 'Mod delete?'}
                    </span>
                    <button
                      onClick={handleDelete}
                      disabled={saving}
                      className="p-0.5 rounded text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Check className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => setConfirming(false)}
                      className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirming(true)}
                    title={isOwn ? 'Delete' : 'Moderator delete'}
                    className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100 group-data-[actions-active=true]:opacity-100 h-[26px] w-[26px]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Tiny amber "Creator" badge rendered alongside the display name when
 *  the sender is the grid's onchain creator. Uppercase monospace keeps
 *  it visually distinct from the level badge (which uses the same amber
 *  palette but a rounded pill) and makes it scannable at the 11px copy
 *  size used by the message header. */
function CreatorChip() {
  return (
    <span
      className="shrink-0 rounded-[2px] border border-amber-400/50 bg-amber-400/10 px-1 py-[1px] font-mono text-[8.5px] uppercase tracking-[0.18em] text-amber-200"
      title="Grid creator"
    >
      Creator
    </span>
  );
}

/** Cyan twin of `CreatorChip` for wallets on the Dev allow-list. Same
 *  geometry as `CreatorChip` so the two stack cleanly when one user is
 *  both the team and the onchain creator; the cyan / amber split keeps
 *  the roles glanceable without a tooltip. */
function DevChip() {
  return (
    <span
      className="shrink-0 rounded-[2px] border border-cyan-400/50 bg-cyan-400/10 px-1 py-[1px] font-mono text-[8.5px] uppercase tracking-[0.18em] text-cyan-200"
      title="Takeover team"
    >
      Dev
    </span>
  );
}
